import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { humanizeKey, isPlainObject } from "./analysisUtils";
import { useWebSocket } from "../../hooks/useWebSocket";
import LoadingCard from "../LoadingCard";

const QUERY_KEY_OPTIONS = [
  { key: "study_design", label: "Study Design", type: "device" },
  { key: "population", label: "Population", type: "device" },
  { key: "intervention", label: "Intervention", type: "device" },
  { key: "safety", label: "Safety", type: "device" },
  { key: "risk_limitations", label: "Study Limitations", type: "device" },
  { key: "claims", label: "Claims", type: "device" },
  { key: "follow_up_duration", label: "Follow-up Duration", type: "device" },
  { key: "evidence_strength", label: "Evidence Strength", type: "device" },
  { key: "effectiveness", label: "Effectiveness", type: "device" },
  { key: "endpoint_primary", label: "Primary Endpoint", type: "device" },
  { key: "adverse_events", label: "Adverse Events", type: "device" },
  { key: "performance", label: "Performance", type: "device" },
  { key: "clinical_endpoint", label: "Clinical Endpoint", type: "device" },
  { key: "technical_endpoint", label: "Technical Endpoint", type: "device" },
  { key: "device_configuration", label: "Device Configuration", type: "device" },
  { key: "study_design", label: "Study Design", type: "drug" },
  { key: "population", label: "Population", type: "drug" },
  { key: "intervention", label: "Intervention", type: "drug" },
  { key: "safety", label: "Safety", type: "drug" },
  { key: "risk_limitations", label: "Study Limitations", type: "drug" },
  { key: "claims", label: "Author Claims", type: "drug" },
  { key: "follow_up_duration", label: "Follow-up Duration", type: "drug" },
  { key: "evidence_strength", label: "Evidence Strength", type: "drug" },
  { key: "effectiveness", label: "Effectiveness", type: "drug" },
  { key: "dosage_regimen", label: "Dosage Regimen", type: "drug" },
  { key: "endpoint_primary", label: "Primary Endpoint", type: "drug" },
  { key: "adverse_events", label: "Adverse Events", type: "drug" },
];

const LiteratureComparator = ({ analysisJson, productName, productType }) => {
  const { connectWebSocket } = useWebSocket();
  const statusIntervalRef = useRef(null);
  const queryDetailsRef = useRef(null);
  const filesDetailsRef = useRef(null);
  const [queryKey, setQueryKey] = useState("");
  const [querySearch, setQuerySearch] = useState("");
  const [selectedCompareFiles, setSelectedCompareFiles] = useState([]);
  const [comparatorLoading, setComparatorLoading] = useState(false);
  const [comparatorStatusIndex, setComparatorStatusIndex] = useState(0);
  const [comparatorResult, setComparatorResult] = useState(null);
  const [expandedCells, setExpandedCells] = useState({});

  const analysisByFile = useMemo(() => {
    if (!analysisJson || !isPlainObject(analysisJson)) return null;
    const raw =
      analysisJson.deep_analysis ??
      analysisJson.deepAnalysis ??
      analysisJson.result ??
      analysisJson.data ??
      analysisJson;
    return isPlainObject(raw) ? raw : null;
  }, [analysisJson]);

  const isDrug =
    String(productType || "").toLowerCase() === "drug" || productType === true;

  const queryOptions = useMemo(() => {
    return QUERY_KEY_OPTIONS.filter((opt) =>
      isDrug ? opt.type === "drug" : opt.type === "device",
    );
  }, [isDrug]);

  const queryKeyLabel = useMemo(() => {
    return queryOptions.find((opt) => opt.key === queryKey)?.label ?? "Select";
  }, [queryKey, queryOptions]);

  const filteredQueryOptions = useMemo(() => {
    if (!querySearch.trim()) return queryOptions;
    const q = querySearch.trim().toLowerCase();
    return queryOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) || opt.key.toLowerCase().includes(q),
    );
  }, [querySearch, queryOptions]);

  useEffect(() => {
    if (!queryKey && queryOptions.length > 0) {
      setQueryKey(queryOptions[0].key);
    }
  }, [queryKey, queryOptions]);

  useEffect(() => {
    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const queryEl = queryDetailsRef.current;
      const filesEl = filesDetailsRef.current;
      if (
        queryEl &&
        !queryEl.contains(event.target) &&
        filesEl &&
        !filesEl.contains(event.target)
      ) {
        if (queryEl.open) queryEl.open = false;
        if (filesEl.open) filesEl.open = false;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pmidToFileName = useMemo(() => {
    const map = new Map();
    if (!analysisByFile) return map;
    Object.entries(analysisByFile).forEach(([fileName, fileData]) => {
      if (!isPlainObject(fileData)) return;
      for (const value of Object.values(fileData)) {
        if (value && typeof value === "object") {
          const pmid =
            value.pmid ??
            value.PMID ??
            value.response?.pmid ??
            value.response?.PMID ??
            null;
          if (pmid && !map.has(String(pmid))) {
            map.set(String(pmid), fileName);
          }
        }
      }
    });
    return map;
  }, [analysisByFile]);

  const comparatorRows = useMemo(() => {
    if (!comparatorResult) return [];
    const validated =
      comparatorResult?.validated_output ??
      comparatorResult?.validatedOutput ??
      null;
    if (!validated || typeof validated !== "object") return [];
    return Object.entries(validated).map(([pmidKey, row]) => {
      const pmid = row?.pmid ?? row?.PMID ?? row?.pmid_key ?? row?.pmidKey ?? pmidKey ?? "—";
      const fileName = pmidToFileName.get(String(pmid)) || "—";
      return { ...(row || {}), __pmid: pmid, __file_name: fileName };
    });
  }, [comparatorResult, pmidToFileName]);

  const getRowStructuredOutput = (row) => {
    const structured = row?.structured_output ?? row?.structuredOutput ?? null;
    if (structured && isPlainObject(structured)) return structured;
    if (!row || typeof row !== "object") return {};
    const fallback = {};
    Object.entries(row).forEach(([key, value]) => {
      if (key === "pmid" || key === "PMID" || key === "pmid_key" || key === "pmidKey") {
        return;
      }
      if (key.startsWith("__") || key === "structured_output" || key === "structuredOutput") {
        return;
      }
      if (typeof value === "object" && value !== null) return;
      fallback[key] = value;
    });
    return fallback;
  };

  const dynamicKeys = useMemo(() => {
    const keySet = new Set();
    comparatorRows.forEach((row) => {
      const payload = getRowStructuredOutput(row);
      Object.keys(payload).forEach((key) => keySet.add(key));
    });
    return Array.from(keySet);
  }, [comparatorRows]);

  const findPmidForFile = (fileKey) => {
    const fileData = analysisByFile?.[fileKey];
    if (fileData && isPlainObject(fileData)) {
      for (const value of Object.values(fileData)) {
        if (value && typeof value === "object") {
          if (value.pmid) return value.pmid;
          if (value.PMID) return value.PMID;
          if (value.response?.pmid) return value.response.pmid;
          if (value.response?.PMID) return value.response.PMID;
        }
      }
    }
    return "—";
  };

  const fileEntries = useMemo(() => {
    if (!analysisByFile) return [];
    return Object.keys(analysisByFile).map((key) => {
      const pmid = findPmidForFile(key);
      return { key, pmid };
    });
  }, [analysisByFile]);

  const fileKeys = useMemo(() => fileEntries.map((entry) => entry.key), [fileEntries]);

  useEffect(() => {
    setSelectedCompareFiles((prev) => {
      const existing = prev.filter((key) => fileKeys.includes(key));
      if (existing.length > 0) return existing.slice(0, 5);
      return fileKeys.slice(0, 5);
    });
  }, [fileKeys]);

  const pmidListLabel = useMemo(() => {
    if (!selectedCompareFiles.length) return "";
    return selectedCompareFiles.map((fileKey) => findPmidForFile(fileKey)).join(", ");
  }, [selectedCompareFiles]);

  const getPmidFromFileKey = (fileKey) => {
    const pmid = findPmidForFile(fileKey);
    return pmid && pmid !== "—" ? pmid : fileKey;
  };

  const fileLabel = (fileKey) => {
    const pmid = findPmidForFile(fileKey);
    return `${pmid}: ${fileKey}`;
  };

  const getRowPmid = (row) =>
    row?.__pmid ?? row?.pmid ?? row?.PMID ?? row?.pmid_key ?? row?.pmidKey ?? "—";

  const getRowFileName = (row) =>
    row?.__file_name ??
    row?.file_name ??
    row?.fileName ??
    row?.filename ??
    row?.file_key ??
    row?.fileKey ??
    "—";

  const truncateFileName = (name) => {
    if (!name) return "—";
    if (name.length <= 25) return name;
    return `${name.slice(0, 22)}...`;
  };

  const toggleCompareFile = (fileKey) => {
    setSelectedCompareFiles((prev) => {
      if (prev.includes(fileKey)) return prev.filter((key) => key !== fileKey);
      if (prev.length >= 5) {
        toast.error("You can compare up to 5 files.");
        return prev;
      }
      return [...prev, fileKey];
    });
  };

  const runComparator = () => {
    if (!productName) {
      toast.error("No device selected.");
      return;
    }
    if (!queryKey) {
      toast.error("Select a query key.");
      return;
    }
    if (selectedCompareFiles.length === 0) {
      toast.error("Select at least one file to compare.");
      return;
    }

    setComparatorLoading(true);
    setComparatorResult(null);
    setComparatorStatusIndex(0);
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
    }
    statusIntervalRef.current = setInterval(() => {
      setComparatorStatusIndex((prev) => Math.min(prev + 1, 2));
    }, 3000);

    connectWebSocket(
      "generate_litr_comparator",
      {
        product_name: productName,
        query_key: queryKey,
        pmids: selectedCompareFiles.map(getPmidFromFileKey),
      },
      (parsed) => {
        console.log("Literature comparator response:", parsed);
        const outputPayload = parsed?.output ?? parsed;
        console.log("Literature comparator resolved output:", outputPayload);
        setComparatorResult(outputPayload);
        setComparatorLoading(false);
        if (statusIntervalRef.current) {
          clearInterval(statusIntervalRef.current);
        }
        const validated =
          outputPayload?.validated_output ??
          outputPayload?.validatedOutput ??
          null;
        const rows = validated && typeof validated === "object" ? Object.values(validated) : [];
        const hasData = rows.length > 0;
        if (!hasData) {
          toast.error("No comparator data returned.");
        }
      },
      () => {
        setComparatorLoading(false);
        if (statusIntervalRef.current) {
          clearInterval(statusIntervalRef.current);
        }
      },
      () => {
        setComparatorLoading(false);
        if (statusIntervalRef.current) {
          clearInterval(statusIntervalRef.current);
        }
      },
    );
  };

  const cellText = (value) => {
    if (value == null || String(value).trim() === "") {
      return "Relevant Data Not Found";
    }
    return String(value);
  };

  const downloadComparison = () => {
    if (!comparatorRows.length) {
      toast.error("Run the comparator before downloading.");
      return;
    }
    const headers = ["PMID", "File Name", ...dynamicKeys.map(humanizeKey)];
    const rows = comparatorRows.map((row) => {
      const structured = getRowStructuredOutput(row);
      return [
        cellText(getRowPmid(row)),
        cellText(getRowFileName(row)),
        ...dynamicKeys.map((key) => cellText(structured?.[key])),
      ];
    });
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    const sheetName = String(queryKeyLabel || "Comparison")
      .replace(/[\\/?*[\]:]/g, " ")
      .trim()
      .slice(0, 31) || "Comparison";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const safeProduct = String(productName || "literature").replace(/[<>:"/\\|?*]+/g, "_");
    const safeKey = String(queryKey || "comparison").replace(/[<>:"/\\|?*]+/g, "_");
    XLSX.writeFile(workbook, `${safeProduct}_${safeKey}_comparison.xlsx`);
  };

  const renderHighlightedText = (text) => {
    const parts = String(text).split(/(\d+(?:\.\d+)?%?)/g);
    return parts.map((part, idx) => {
      if (/^\d+(?:\.\d+)?%?$/.test(part)) {
        return (
          <span key={idx} className="badge badge-outline badge-sm mx-0.5">
            {part}
          </span>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const renderCellValue = (value, cellKey) => {
    const display = cellText(value);
    const isEmpty = display === "Relevant Data Not Found";

    if (isEmpty) {
      return (
        <span className="text-base-content/50 italic">
          Relevant Data Not Found
        </span>
      );
    }

    return (
      <div>{renderHighlightedText(display)}</div>
    );
  };

  if (!analysisByFile || Object.keys(analysisByFile).length === 0) {
    return (
      <div className="rounded-xl border border-base-300 bg-base-100 p-6 text-base-content/70">
        No analysis data available for comparison.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-base-300 bg-base-100 p-4 shadow-sm">
      <div className="sticky top-0 z-30 bg-base-100 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-base-content">
              Literature Comparator
            </h2>
            <div className="text-sm text-base-content/70">
              Product: {productName || "—"}
            </div>
          </div>
          <div className="text-sm text-base-content/70">
            Selected files: {selectedCompareFiles.length}/5
          </div>
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-[1.2fr_1fr_1fr_auto]">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-base-content/70">
              Query Key
            </span>
            <details className="dropdown relative z-30" ref={queryDetailsRef}>
              <summary className="btn btn-outline btn-sm w-full justify-between">
                Clinical Domain: {queryKeyLabel}
              </summary>
              <div className="dropdown-content z-40 mt-2 w-full rounded-xl border border-base-300 bg-base-100 p-3 shadow">
                <input
                  type="text"
                  className="input input-bordered input-sm w-full"
                  placeholder="Search query keys"
                  value={querySearch}
                  onChange={(e) => setQuerySearch(e.target.value)}
                />
                <ul className="mt-2 max-h-64 overflow-y-auto text-sm">
                  {filteredQueryOptions.map((opt) => (
                    <li key={opt.key}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between rounded px-2 py-1 hover:bg-base-200"
                        onClick={() => {
                          setQueryKey(opt.key);
                        }}
                      >
                        <span>{opt.label}</span>
                        {queryKey === opt.key && (
                          <span className="text-xs text-primary">Selected</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-base-content/70">
              Files
            </span>
            <details className="dropdown relative z-30" ref={filesDetailsRef}>
              <summary className="btn btn-outline btn-sm w-full justify-between">
                Select files ({selectedCompareFiles.length}/5)
              </summary>
              <div className="dropdown-content z-40 mt-2 w-full rounded-xl border border-base-300 bg-base-100 p-3 shadow">
                <ul className="max-h-64 overflow-y-auto text-sm">
                  {fileKeys.map((fileKey) => (
                    <li key={fileKey}>
                      <label className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 hover:bg-base-200">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-xs mt-0.5"
                          checked={selectedCompareFiles.includes(fileKey)}
                          onChange={() => toggleCompareFile(fileKey)}
                        />
                        <span className="truncate" title={fileLabel(fileKey)}>
                          {fileLabel(fileKey)}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-base-content/70">
              Run
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm w-full"
              onClick={runComparator}
              disabled={comparatorLoading}
            >
              Run Comparator
            </button>
          </div>
        </div>

        {comparatorLoading && (
          <div className="mt-4">
            <LoadingCard
              message={`Comparing ${queryKeyLabel} for PMIDs: ${pmidListLabel || "—"}`}
              subMessage="Retrieving chunks, validating evidence, and building comparison."
            />
          </div>
        )}
      </div>

      {!comparatorLoading && selectedCompareFiles.length > 0 && (
        <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {selectedCompareFiles.map((fileKey) => (
            <button
              key={fileKey}
              type="button"
              className="rounded-xl border border-base-300 bg-base-100 p-3 text-left shadow-sm hover:border-primary/50"
            >
              <div className="text-xs text-base-content/70">PMID</div>
              <div className="text-sm text-base-content">{findPmidForFile(fileKey)}</div>
              <div className="mt-1 text-[10px] text-base-content/60 truncate" title={fileKey}>
                {fileKey}
              </div>
            </button>
          ))}
        </div>
      )}

      {!comparatorLoading && comparatorRows.length > 0 && (
        <div className="relative z-0 mt-4">
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              className="btn btn-primary tooltip tooltip-left"
              data-tip="Download the comparison table in Excel format"
              onClick={downloadComparison}
            >
              Export
            </button>
          </div>
          <div className="max-h-[calc(100vh-320px)] overflow-auto rounded-xl border border-base-300 bg-base-200">
          <table className="table whitespace-normal">
            <thead className="sticky top-0 z-10 bg-base-300">
              <tr>
                <th className="sticky left-0 z-20 bg-base-300">PMID</th>
                <th className="bg-base-300">File Name</th>
                {dynamicKeys.map((key) => (
                  <th key={key} className="min-w-[220px] bg-base-300">
                    {humanizeKey(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparatorRows.map((row, idx) => {
                const structured = getRowStructuredOutput(row);
                const pmid = getRowPmid(row);
                const fileName = getRowFileName(row);
                return (
                  <tr key={`${pmid}-${idx}`}>
                    <td className="sticky left-0 z-10 bg-base-100 font-semibold align-top">
                      {pmid}
                    </td>
                    <td className="align-top whitespace-normal break-words" title={fileName}>
                      {truncateFileName(fileName)}
                    </td>
                    {dynamicKeys.map((key) => {
                      const value = structured?.[key];
                      const cellKey = `${pmid}-${key}`;
                      return (
                        <td key={cellKey} className="min-w-[220px] align-top whitespace-normal break-words">
                          {renderCellValue(value, cellKey)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiteratureComparator;
